import React, { createContext, useContext, useReducer, useEffect, useCallback, type ReactNode } from 'react';
import type { Property, Unit, Tenant, Lease, RentPayment, PaymentAllocation, LateFee, Expense, Income, MaintenanceRequest, UtilityAccount } from '../types';
import {
  propertiesApi,
  allocationsApi,
  lateFeesApi,
  type PaymentCreateData,
  type PaymentUpdateData,
  unitsApi,
  tenantsApi,
  leasesApi,
  paymentsApi,
  expensesApi,
  incomesApi,
  maintenanceApi,
  utilityAccountsApi,
} from '../lib/api';
import { useAuth } from './AuthContext';
import { todayLocalDate } from '../lib/utils';

interface AppState {
  properties: Property[];
  units: Unit[];
  tenants: Tenant[];
  leases: Lease[];
  rentPayments: RentPayment[];
  paymentAllocations: PaymentAllocation[];
  lateFees: LateFee[];
  expenses: Expense[];
  incomes: Income[];
  maintenance: MaintenanceRequest[];
  utilityAccounts: UtilityAccount[];
  isLoading: boolean;
  error: string | null;
}

type Action =
  | { type: 'SET_STATE'; payload: Partial<AppState> }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'ADD_PROPERTY'; payload: Property }
  | { type: 'UPDATE_PROPERTY'; payload: Property }
  | { type: 'DELETE_PROPERTY'; payload: string }
  | { type: 'ADD_UNIT'; payload: Unit }
  | { type: 'UPDATE_UNIT'; payload: Unit }
  | { type: 'DELETE_UNIT'; payload: string }
  | { type: 'ADD_TENANT'; payload: Tenant }
  | { type: 'UPDATE_TENANT'; payload: Tenant }
  | { type: 'DELETE_TENANT'; payload: string }
  | { type: 'ADD_LEASE'; payload: Lease }
  | { type: 'UPDATE_LEASE'; payload: Lease }
  | { type: 'DELETE_LEASE'; payload: string }
  | { type: 'ADD_EXPENSE'; payload: Expense }
  | { type: 'UPDATE_EXPENSE'; payload: Expense }
  | { type: 'DELETE_EXPENSE'; payload: string }
  | { type: 'ADD_INCOME'; payload: Income }
  | { type: 'UPDATE_INCOME'; payload: Income }
  | { type: 'DELETE_INCOME'; payload: string }
  | { type: 'ADD_RENT_PAYMENT'; payload: RentPayment }
  | { type: 'UPDATE_PAYMENT_STATUS'; payload: RentPayment }
  | { type: 'DELETE_RENT_PAYMENT'; payload: string }
  | { type: 'ADD_MAINTENANCE'; payload: MaintenanceRequest }
  | { type: 'UPDATE_MAINTENANCE'; payload: MaintenanceRequest }
  | { type: 'DELETE_MAINTENANCE'; payload: string }
  | { type: 'ADD_UTILITY'; payload: UtilityAccount }
  | { type: 'UPDATE_UTILITY'; payload: UtilityAccount }
  | { type: 'DELETE_UTILITY'; payload: string }
  | { type: 'SET_ALLOCATIONS'; payload: PaymentAllocation[] }
  | { type: 'ADD_ALLOCATIONS'; payload: PaymentAllocation[] }
  | { type: 'SET_LATE_FEES'; payload: LateFee[] }
  | { type: 'ADD_LATE_FEE'; payload: LateFee }
  | { type: 'UPDATE_LATE_FEE'; payload: LateFee }
  | { type: 'DELETE_LATE_FEE'; payload: string };

const initialState: AppState = {
  properties: [],
  units: [],
  tenants: [],
  leases: [],
  rentPayments: [],
  paymentAllocations: [],
  lateFees: [],
  expenses: [],
  incomes: [],
  maintenance: [],
  utilityAccounts: [],
  isLoading: false,
  error: null,
};

function reducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case 'SET_STATE':
      return { ...state, ...action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'ADD_PROPERTY':
      return { ...state, properties: [...state.properties, action.payload] };
    case 'UPDATE_PROPERTY':
      return {
        ...state,
        properties: state.properties.map(p => p.id === action.payload.id ? action.payload : p),
      };
    case 'DELETE_PROPERTY':
      return {
        ...state,
        properties: state.properties.filter(p => p.id !== action.payload),
        units: state.units.filter(u => u.propertyId !== action.payload),
      };
    case 'ADD_UNIT':
      return { ...state, units: [...state.units, action.payload] };
    case 'UPDATE_UNIT':
      return {
        ...state,
        units: state.units.map(u => u.id === action.payload.id ? action.payload : u),
      };
    case 'DELETE_UNIT':
      return {
        ...state,
        units: state.units.filter(u => u.id !== action.payload),
      };
    case 'ADD_TENANT':
      return { ...state, tenants: [...state.tenants, action.payload] };
    case 'UPDATE_TENANT':
      return {
        ...state,
        tenants: state.tenants.map(t => t.id === action.payload.id ? action.payload : t),
      };
    case 'DELETE_TENANT':
      return {
        ...state,
        tenants: state.tenants.filter(t => t.id !== action.payload),
      };
    case 'ADD_LEASE':
      return { ...state, leases: [action.payload, ...state.leases] };
    case 'UPDATE_LEASE':
      return {
        ...state,
        leases: state.leases.map(l => (l.id === action.payload.id ? action.payload : l)),
      };
    case 'DELETE_LEASE':
      return { ...state, leases: state.leases.filter(l => l.id !== action.payload) };
    case 'ADD_EXPENSE':
      return { ...state, expenses: [...state.expenses, action.payload] };
    case 'UPDATE_EXPENSE':
      return {
        ...state,
        expenses: state.expenses.map(e => e.id === action.payload.id ? action.payload : e),
      };
    case 'DELETE_EXPENSE':
      return {
        ...state,
        expenses: state.expenses.filter(e => e.id !== action.payload),
      };
    case 'ADD_INCOME':
      return { ...state, incomes: [...state.incomes, action.payload] };
    case 'UPDATE_INCOME':
      return { ...state, incomes: state.incomes.map(i => i.id === action.payload.id ? action.payload : i) };
    case 'DELETE_INCOME':
      return { ...state, incomes: state.incomes.filter(i => i.id !== action.payload) };
    case 'ADD_RENT_PAYMENT':
      return { ...state, rentPayments: [...state.rentPayments, action.payload] };
    case 'UPDATE_PAYMENT_STATUS':
      return {
        ...state,
        rentPayments: state.rentPayments.map(p =>
          p.id === action.payload.id ? action.payload : p
        ),
      };
    case 'DELETE_RENT_PAYMENT':
      return { ...state, rentPayments: state.rentPayments.filter(p => p.id !== action.payload) };
    case 'ADD_MAINTENANCE':
      return { ...state, maintenance: [action.payload, ...state.maintenance] };
    case 'UPDATE_MAINTENANCE':
      return {
        ...state,
        maintenance: state.maintenance.map(m => m.id === action.payload.id ? action.payload : m),
      };
    case 'DELETE_MAINTENANCE':
      // Also drop the derived maintenance expense (id = maint-<requestId>) the
      // "mark paid" step created, so Finances and the Dashboard stop counting it
      // the moment the report is deleted, matching the server-side cleanup.
      return {
        ...state,
        maintenance: state.maintenance.filter(m => m.id !== action.payload),
        expenses: state.expenses.filter(e => e.id !== `maint-${action.payload}`),
      };
    case 'ADD_UTILITY':
      return { ...state, utilityAccounts: [action.payload, ...state.utilityAccounts] };
    case 'UPDATE_UTILITY':
      return {
        ...state,
        utilityAccounts: state.utilityAccounts.map(u => u.id === action.payload.id ? action.payload : u),
      };
    case 'DELETE_UTILITY':
      return { ...state, utilityAccounts: state.utilityAccounts.filter(u => u.id !== action.payload) };
    case 'SET_ALLOCATIONS':
      return { ...state, paymentAllocations: action.payload };
    case 'ADD_ALLOCATIONS': {
      // Replace any existing allocations for the same payment, then add new ones.
      const newPaymentIds = new Set(action.payload.map(a => a.paymentId));
      const kept = state.paymentAllocations.filter(a => !newPaymentIds.has(a.paymentId));
      return { ...state, paymentAllocations: [...kept, ...action.payload] };
    }
    case 'SET_LATE_FEES':
      return { ...state, lateFees: action.payload };
    case 'ADD_LATE_FEE':
      return { ...state, lateFees: [...state.lateFees, action.payload] };
    case 'UPDATE_LATE_FEE':
      return { ...state, lateFees: state.lateFees.map(lf => lf.id === action.payload.id ? action.payload : lf) };
    case 'DELETE_LATE_FEE':
      return { ...state, lateFees: state.lateFees.filter(lf => lf.id !== action.payload) };
    default:
      return state;
  }
}

interface AppContextType extends AppState {
  dispatch: React.Dispatch<Action>;
  refreshData: () => Promise<void>;
  getPropertyUnits: (propertyId: string) => Unit[];
  getUnitLease: (unitId: string) => Lease | undefined;
  getLeaseTenants: (leaseId: string) => Tenant[];
  getTenantLeases: (tenantId: string) => Lease[];
  addProperty: (property: Omit<Property, 'id'>) => Promise<Property>;
  updateProperty: (property: Property) => Promise<void>;
  deleteProperty: (id: string) => Promise<void>;
  addUnit: (unit: Omit<Unit, 'id'>) => Promise<Unit>;
  updateUnit: (unit: Unit) => Promise<void>;
  deleteUnit: (id: string) => Promise<void>;
  addTenant: (tenant: Omit<Tenant, 'id'>) => Promise<Tenant>;
  updateTenant: (tenant: Tenant) => Promise<void>;
  deleteTenant: (id: string) => Promise<void>;
  addLease: (lease: Omit<Lease, 'id'> & { pausedAt?: string }) => Promise<Lease>;
  updateLease: (lease: Lease & { statusChangedOn?: string }) => Promise<void>;
  deleteLease: (id: string) => Promise<void>;
  addExpense: (expense: Omit<Expense, 'id'>) => Promise<Expense>;
  updateExpense: (expense: Expense) => Promise<void>;
  deleteExpense: (id: string) => Promise<void>;
  addUtilityAccount: (account: Omit<UtilityAccount, 'id'>) => Promise<void>;
  updateUtilityAccount: (account: UtilityAccount) => Promise<void>;
  deleteUtilityAccount: (id: string) => Promise<void>;
  addIncome: (income: Omit<Income, 'id'>) => Promise<void>;
  updateIncome: (income: Income) => Promise<void>;
  deleteIncome: (id: string) => Promise<void>;
  addRentPayment: (payment: PaymentCreateData, opts?: { deferSheetSync?: boolean }) => Promise<void>;
  updatePaymentStatus: (id: string, status: RentPayment['status'], paymentDetails?: { receivedDate?: string; paymentMethod?: RentPayment['paymentMethod']; uploadedBy?: string }) => Promise<void>;
  updateRentPayment: (payment: PaymentUpdateData) => Promise<void>;
  deleteRentPayment: (id: string) => Promise<void>;
  addLateFee: (data: { leaseId: string; month: number; year: number; amount: number; assessedDate: string; tenantId?: string; notes?: string }) => Promise<void>;
  updateLateFee: (data: Partial<LateFee> & { id: string }) => Promise<void>;
  waiveLateFee: (id: string, reason?: string) => Promise<void>;
  deleteLateFee: (id: string) => Promise<void>;
  addMaintenance: (request: Omit<MaintenanceRequest, 'id'>) => Promise<void>;
  updateMaintenance: (request: MaintenanceRequest) => Promise<void>;
  deleteMaintenance: (id: string) => Promise<void>;
  resetData: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { isAuthenticated } = useAuth();

  const refreshData = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });
    try {
      const [properties, units, tenants, leases, rentPayments, expenses, incomes, maintenance, utilityAccounts, paymentAllocations, lateFees] = await Promise.all([
        propertiesApi.getAll(),
        unitsApi.getAll(),
        tenantsApi.getAll(),
        leasesApi.getAll(),
        paymentsApi.getAll(),
        expensesApi.getAll(),
        incomesApi.getAll(),
        maintenanceApi.getAll(),
        utilityAccountsApi.getAll(),
        allocationsApi.getAll().catch(() => [] as PaymentAllocation[]),
        lateFeesApi.getAll().catch(() => [] as LateFee[]),
      ]);
      dispatch({
        type: 'SET_STATE',
        payload: { properties, units, tenants, leases, rentPayments, paymentAllocations, lateFees, expenses, incomes, maintenance, utilityAccounts, isLoading: false },
      });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message });
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  // Load data once the user is authenticated. When they sign out, clear it so
  // no stale data lingers and we never fire unauthenticated (401) requests.
  useEffect(() => {
    if (isAuthenticated) {
      refreshData();
    } else {
      dispatch({
        type: 'SET_STATE',
        payload: {
          properties: [], units: [], tenants: [], leases: [], rentPayments: [], paymentAllocations: [], lateFees: [], expenses: [], incomes: [], maintenance: [], utilityAccounts: [],
          isLoading: false, error: null,
        },
      });
    }
  }, [isAuthenticated, refreshData]);

  const getPropertyUnits = useCallback((propertyId: string) =>
    state.units.filter(u => u.propertyId === propertyId), [state.units]);

  const getUnitLease = useCallback(
    (unitId: string) => state.leases.find(l => l.unitId === unitId && l.status !== 'ended'),
    [state.leases]
  );

  const getLeaseTenants = useCallback(
    (leaseId: string) => {
      const lease = state.leases.find(l => l.id === leaseId);
      if (!lease) return [];
      return state.tenants.filter(t => lease.tenantIds.includes(t.id));
    },
    [state.leases, state.tenants]
  );

  const getTenantLeases = useCallback(
    (tenantId: string) => state.leases.filter(l => l.tenantIds.includes(tenantId)),
    [state.leases]
  );

  const addProperty = async (property: Omit<Property, 'id'>) => {
    const newProperty = await propertiesApi.create(property);
    dispatch({ type: 'ADD_PROPERTY', payload: newProperty });
    return newProperty;
  };

  const updateProperty = async (property: Property) => {
    const updated = await propertiesApi.update(property.id, property);
    dispatch({ type: 'UPDATE_PROPERTY', payload: updated });
  };

  const deleteProperty = async (id: string) => {
    await propertiesApi.delete(id);
    dispatch({ type: 'DELETE_PROPERTY', payload: id });
  };

  const addUnit = async (unit: Omit<Unit, 'id'>) => {
    const newUnit = await unitsApi.create(unit);
    dispatch({ type: 'ADD_UNIT', payload: newUnit });
    return newUnit;
  };

  const updateUnit = async (unit: Unit) => {
    const updated = await unitsApi.update(unit.id, unit);
    dispatch({ type: 'UPDATE_UNIT', payload: updated });
  };

  const deleteUnit = async (id: string) => {
    await unitsApi.delete(id);
    dispatch({ type: 'DELETE_UNIT', payload: id });
  };

  const addTenant = async (tenant: Omit<Tenant, 'id'>) => {
    const created = await tenantsApi.create(tenant);
    dispatch({ type: 'ADD_TENANT', payload: created });
    return created;
  };

  const updateTenant = async (tenant: Tenant) => {
    const updated = await tenantsApi.update(tenant.id, tenant);
    dispatch({ type: 'UPDATE_TENANT', payload: updated });
  };

  const deleteTenant = async (id: string) => {
    await tenantsApi.delete(id);
    dispatch({ type: 'DELETE_TENANT', payload: id });
  };

  const addLease = async (lease: Omit<Lease, 'id'> & { pausedAt?: string }) => {
    const created = await leasesApi.create(lease);
    dispatch({ type: 'ADD_LEASE', payload: created });
    return created;
  };

  const updateLease = async (lease: Lease & { statusChangedOn?: string }) => {
    const updated = await leasesApi.update(lease.id, lease);
    dispatch({ type: 'UPDATE_LEASE', payload: updated });
  };

  const deleteLease = async (id: string) => {
    await leasesApi.delete(id);
    dispatch({ type: 'DELETE_LEASE', payload: id });
  };

  const addExpense = async (expense: Omit<Expense, 'id'>): Promise<Expense> => {
    const newExpense = await expensesApi.create(expense);
    dispatch({ type: 'ADD_EXPENSE', payload: newExpense });
    return newExpense;
  };

  const updateExpense = async (expense: Expense) => {
    const updated = await expensesApi.update(expense.id, expense);
    dispatch({ type: 'UPDATE_EXPENSE', payload: updated });
  };

  const addIncome = async (income: Omit<Income, 'id'>) => {
    const newIncome = await incomesApi.create(income);
    dispatch({ type: 'ADD_INCOME', payload: newIncome });
  };

  const deleteExpense = async (id: string) => {
    await expensesApi.delete(id);
    dispatch({ type: 'DELETE_EXPENSE', payload: id });
  };

  const addUtilityAccount = async (data: Omit<UtilityAccount, 'id'>) => {
    const created = await utilityAccountsApi.create(data);
    dispatch({ type: 'ADD_UTILITY', payload: created });
  };

  const updateUtilityAccount = async (account: UtilityAccount) => {
    const updated = await utilityAccountsApi.update(account.id, account);
    dispatch({ type: 'UPDATE_UTILITY', payload: updated });
  };

  const deleteUtilityAccount = async (id: string) => {
    await utilityAccountsApi.delete(id);
    dispatch({ type: 'DELETE_UTILITY', payload: id });
  };

  const updateIncome = async (income: Income) => {
    const updated = await incomesApi.update(income.id, income);
    dispatch({ type: 'UPDATE_INCOME', payload: updated });
  };

  const deleteIncome = async (id: string) => {
    await incomesApi.delete(id);
    dispatch({ type: 'DELETE_INCOME', payload: id });
  };

  const addRentPayment = async (payment: PaymentCreateData, opts?: { deferSheetSync?: boolean }) => {
    const result = await paymentsApi.create(payment, opts);
    const { allocations: allocs, ...paymentData } = result as RentPayment & { allocations?: PaymentAllocation[] };
    dispatch({ type: 'ADD_RENT_PAYMENT', payload: paymentData });
    if (allocs && allocs.length > 0) {
      dispatch({ type: 'ADD_ALLOCATIONS', payload: allocs });
    }
  };

  const updatePaymentStatus = async (
    id: string,
    status: RentPayment['status'],
    paymentDetails?: { receivedDate?: string; paymentMethod?: RentPayment['paymentMethod']; uploadedBy?: string }
  ) => {
    const payment = state.rentPayments.find(p => p.id === id);
    if (!payment) return;
    const updated = await paymentsApi.update(id, {
      ...payment,
      status,
      paidDate: status === 'paid' ? todayLocalDate() : undefined,
      receivedDate: paymentDetails?.receivedDate,
      paymentMethod: paymentDetails?.paymentMethod,
      uploadedBy: paymentDetails?.uploadedBy,
      uploadedAt: new Date().toISOString(),
    });
    dispatch({ type: 'UPDATE_PAYMENT_STATUS', payload: updated });
  };

  const updateRentPayment = async (payment: PaymentUpdateData) => {
    const result = await paymentsApi.update(payment.id, payment);
    const { allocations: allocs, ...paymentData } = result as RentPayment & { allocations?: PaymentAllocation[] };
    dispatch({ type: 'UPDATE_PAYMENT_STATUS', payload: paymentData });
    if (allocs) {
      dispatch({ type: 'ADD_ALLOCATIONS', payload: allocs });
    }
  };

  const deleteRentPayment = async (id: string) => {
    await paymentsApi.delete(id);
    dispatch({ type: 'DELETE_RENT_PAYMENT', payload: id });
  };

  const addMaintenance = async (request: Omit<MaintenanceRequest, 'id'>) => {
    const created = await maintenanceApi.create(request);
    dispatch({ type: 'ADD_MAINTENANCE', payload: created });
  };

  const updateMaintenance = async (request: MaintenanceRequest) => {
    const updated = await maintenanceApi.update(request.id, request);
    dispatch({ type: 'UPDATE_MAINTENANCE', payload: updated });
  };

  const deleteMaintenance = async (id: string) => {
    await maintenanceApi.delete(id);
    dispatch({ type: 'DELETE_MAINTENANCE', payload: id });
  };

  const addLateFee = async (data: { leaseId: string; month: number; year: number; amount: number; assessedDate: string; tenantId?: string; notes?: string }) => {
    const created = await lateFeesApi.create(data);
    dispatch({ type: 'ADD_LATE_FEE', payload: created });
  };

  const updateLateFee = async (data: Partial<LateFee> & { id: string }) => {
    const updated = await lateFeesApi.update(data.id, data);
    dispatch({ type: 'UPDATE_LATE_FEE', payload: updated });
  };

  const waiveLateFee = async (id: string, reason?: string) => {
    const updated = await lateFeesApi.waive(id, reason);
    dispatch({ type: 'UPDATE_LATE_FEE', payload: updated });
  };

  const deleteLateFee = async (id: string) => {
    await lateFeesApi.delete(id);
    dispatch({ type: 'DELETE_LATE_FEE', payload: id });
  };

  const resetData = () => {
    dispatch({ type: 'SET_STATE', payload: initialState });
  };

  return (
    <AppContext.Provider
      value={{
        ...state,
        dispatch,
        refreshData,
        getPropertyUnits,
        getUnitLease,
        getLeaseTenants,
        getTenantLeases,
        addProperty,
        updateProperty,
        deleteProperty,
        addUnit,
        updateUnit,
        deleteUnit,
        addTenant,
        updateTenant,
        deleteTenant,
        addLease,
        updateLease,
        deleteLease,
        addExpense,
        updateExpense,
        deleteExpense,
        addUtilityAccount,
        updateUtilityAccount,
        deleteUtilityAccount,
        addIncome,
        updateIncome,
        deleteIncome,
        addRentPayment,
        updatePaymentStatus,
        updateRentPayment,
        deleteRentPayment,
        addLateFee,
        updateLateFee,
        waiveLateFee,
        deleteLateFee,
        addMaintenance,
        updateMaintenance,
        deleteMaintenance,
        resetData,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

// The hook is co-located with its provider on purpose; splitting it out only to
// satisfy a dev-only fast-refresh rule would churn every import site.
// eslint-disable-next-line react-refresh/only-export-components
export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
